import { useState } from 'react'
import { useParams, useNavigate, Link } from '@tanstack/react-router'
import { cn } from '@/libs/utils'
import {
  ArrowLeftIcon,
  CheckIcon,
  XIcon,
  MoreVerticalIcon,
  Eye,
  Edit,
  Send,
} from 'lucide-react'
import {
  useTemplateServiceGetTemplatesByTemplateIdQuery,
  useTemplateServiceApproveTemplateMutation,
  useTemplateServiceRejectTemplateMutation,
  useTemplateServiceSubmitTemplateMutation,
} from 'zma-react-sdk/template-service'
import { useToast } from '@/hooks/use-toast'
import { useTranslation } from '@/hooks/use-translation'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

export function TemplateDetailPage() {
  const { templateId } = useParams({ strict: false }) as { templateId: string }
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { toast } = useToast()
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [selectedVersionId, setSelectedVersionId] = useState('')
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null)

  // Get tenantId from localStorage (from table page selection)
  const tenantId =
    typeof window !== 'undefined'
      ? localStorage.getItem('admin_templates_tenantId') || ''
      : ''

  const { data, loading, error, refetch } =
    useTemplateServiceGetTemplatesByTemplateIdQuery({
      variables: { templateId, tenantId },
      skip: !templateId || !tenantId,
    })

  // Approve mutation
  const [approveTemplate, { loading: approving }] =
    useTemplateServiceApproveTemplateMutation({
      onCompleted: () => {
        toast({
          title: t('common.success'),
          description: t('admin.templateApprovedSuccessfully'),
        })
        setOpenPopoverId(null)
        refetch()
      },
      onError: (error) => {
        toast({
          title: t('common.error'),
          description: error.message,
          variant: 'destructive',
        })
      },
    })

  // Reject mutation
  const [rejectTemplate, { loading: rejecting }] =
    useTemplateServiceRejectTemplateMutation({
      onCompleted: () => {
        toast({
          title: t('common.success'),
          description: t('admin.templateRejectedSuccessfully'),
        })
        setShowRejectDialog(false)
        setRejectReason('')
        setSelectedVersionId('')
        setOpenPopoverId(null)
        refetch()
      },
      onError: (error) => {
        toast({
          title: t('common.error'),
          description: error.message,
          variant: 'destructive',
        })
      },
    })

  // Submit mutation
  const [submitTemplate, { loading: submitting }] =
    useTemplateServiceSubmitTemplateMutation({
      onCompleted: () => {
        toast({
          title: t('common.success'),
          description: t('admin.templateSubmittedSuccessfully'),
        })
        setOpenPopoverId(null)
        refetch()
      },
      onError: (error) => {
        toast({
          title: t('common.error'),
          description: error.message,
          variant: 'destructive',
        })
      },
    })

  const handleApprove = async (versionId: string) => {
    await approveTemplate({
      variables: {
        input: { id: versionId },
      },
    })
  }

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast({
        title: t('common.error'),
        description: t('admin.pleaseProvideRejectionReason'),
        variant: 'destructive',
      })
      return
    }

    await rejectTemplate({
      variables: {
        input: {
          id: selectedVersionId,
          rejectedReason: rejectReason,
        },
      },
    })
  }

  const handleSubmit = async (versionId: string) => {
    await submitTemplate({
      variables: {
        input: { id: versionId },
      },
    })
  }

  if (loading) {
    return (
      <div className='flex min-h-screen w-full flex-col bg-gradient-to-br from-emerald-50/30 to-blue-50/30 p-4 md:p-6'>
        <div className='space-y-4'>
          <div className='h-8 w-1/3 animate-pulse rounded bg-gray-200'></div>
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className='h-16 animate-pulse rounded bg-gray-100'
            ></div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='flex min-h-screen w-full flex-col bg-gradient-to-br from-emerald-50/30 to-blue-50/30 p-4 md:p-6'>
        <Card>
          <CardHeader>
            <CardTitle className='text-red-600'>{t('common.error')}</CardTitle>
            <CardDescription>{error.message}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  const versions = data?.templateServiceGetTemplatesByTemplateId || []

  if (versions.length === 0) {
    return (
      <div className='flex min-h-screen w-full flex-col bg-gradient-to-br from-emerald-50/30 to-blue-50/30 p-4 md:p-6'>
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.templateNotFound')}</CardTitle>
            <CardDescription>
              {t('admin.templateNotFoundDescription')}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  // Sort versions descending, filter out nulls
  const sortedVersions = versions
    .filter((v) => v !== null)
    .sort((a, b) => (b!.version || 0) - (a!.version || 0))

  const getStatusBadgeColor = (status?: string | null) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800'
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800'
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800'
      case 'REJECTED':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className='flex min-h-screen w-full flex-col bg-gradient-to-br from-emerald-50/30 to-blue-50/30 p-4 md:p-6'>
      {/* Header */}
      <header className='mb-6'>
        <div className='flex items-center gap-3'>
          <Button
            variant='ghost'
            size='icon'
            onClick={() => navigate({ to: '/admin/templates' as any })}
          >
            <ArrowLeftIcon className='h-5 w-5' />
          </Button>
          <div>
            <h1 className='bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-2xl font-bold text-transparent'>
              {sortedVersions[0]?.templateCode || 'Template'}
            </h1>
            <p className='text-sm text-gray-600'>ID: {templateId}</p>
          </div>
        </div>
      </header>

      {/* Versions Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('admin.templateVersions')}</CardTitle>
          <CardDescription>{t('admin.allVersionsOfTemplate')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className='overflow-x-auto'>
            <table className='min-w-full divide-y divide-gray-200'>
              <thead className='bg-gray-50'>
                <tr>
                  <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                    {t('admin.version')}
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                    {t('admin.name')}
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                    {t('admin.status')}
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                    {t('admin.type')}
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                    {t('admin.createdBy')}
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                    {t('admin.createdAt')}
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'></th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-200 bg-white'>
                {sortedVersions.map((version) => (
                  <tr key={version.id} className='hover:bg-gray-50'>
                    <td className='whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900'>
                      v{version.version}
                    </td>
                    <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                      {version.name}
                    </td>
                    <td className='whitespace-nowrap px-6 py-4'>
                      <Badge
                        className={cn(
                          'font-semibold',
                          getStatusBadgeColor(version.status)
                        )}
                      >
                        {version.status}
                      </Badge>
                    </td>
                    <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                      {version.type}
                    </td>
                    <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                      {version.createdBy || '-'}
                    </td>
                    <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                      {version.createdAt
                        ? new Date(version.createdAt).toLocaleString('vi-VN')
                        : '-'}
                    </td>
                    <td className='whitespace-nowrap px-6 py-4 text-sm font-medium'>
                      <DropdownMenu
                        open={openPopoverId === version.id}
                        onOpenChange={(open) =>
                          setOpenPopoverId(open ? version.id! : null)
                        }
                      >
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='h-8 w-8 p-0'
                          >
                            <span className='sr-only'>Open menu</span>
                            <MoreVerticalIcon className='h-4 w-4' />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end'>
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <Link
                              to='/admin/templates/$templateId/version/$versionId'
                              params={{
                                templateId,
                                versionId: version.id!,
                              }}
                              search={{ edit: false }}
                            >
                              <Eye className='mr-2 h-4 w-4' />
                              {t('admin.viewDetails')}
                            </Link>
                          </DropdownMenuItem>
                          {version.status?.toLowerCase() === 'draft' && (
                            <>
                              <DropdownMenuItem asChild>
                                <Link
                                  to='/admin/templates/$templateId/version/$versionId'
                                  params={{
                                    templateId,
                                    versionId: version.id!,
                                  }}
                                  search={{ edit: true }}
                                >
                                  <Edit className='mr-2 h-4 w-4' />
                                  {t('common.edit')}
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleSubmit(version.id!)}
                                disabled={submitting}
                              >
                                <Send className='mr-2 h-4 w-4' />
                                {t('admin.submit')}
                              </DropdownMenuItem>
                            </>
                          )}
                          {version.status?.toLowerCase() === 'pending' && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleApprove(version.id!)}
                                disabled={approving}
                              >
                                <CheckIcon className='mr-2 h-4 w-4' />
                                {t('admin.approve')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className='text-destructive'
                                onClick={() => {
                                  setSelectedVersionId(version.id!)
                                  setShowRejectDialog(true)
                                  setOpenPopoverId(null)
                                }}
                                disabled={rejecting}
                              >
                                <XIcon className='mr-2 h-4 w-4' />
                                {t('admin.reject')}
                              </DropdownMenuItem>
                            </>
                          )}
                          {version.status?.toLowerCase() === 'approved' && (
                            <>
                              <DropdownMenuItem asChild>
                                <Link
                                  to='/admin/templates/$templateId/version/$versionId'
                                  params={{
                                    templateId,
                                    versionId: version.id!,
                                  }}
                                  search={{ edit: true }}
                                >
                                  <Edit className='mr-2 h-4 w-4' />
                                  {t('common.edit')}
                                </Link>
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.rejectTemplate')}</DialogTitle>
            <DialogDescription>
              {t('admin.pleaseProvideReasonForRejection')}
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <Label htmlFor='reason'>{t('admin.reason')} *</Label>
              <Textarea
                id='reason'
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={t('admin.enterRejectionReason')}
                className='min-h-[100px]'
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setShowRejectDialog(false)
                setRejectReason('')
                setSelectedVersionId('')
              }}
              disabled={rejecting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant='destructive'
              onClick={handleReject}
              disabled={rejecting}
            >
              {rejecting ? t('common.rejecting') : t('admin.reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
